import React, { useEffect, useMemo, useState } from "react";
import { CircularProgress, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import ShowChartOutlinedIcon from "@mui/icons-material/ShowChartOutlined";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import MaterialReactTable from "material-react-table";
import { publicApi } from "../../api/http";
import { nhlGoalieColumns, nhlSkaterColumns } from "./Constants";
import PlayerNhlStatsGraph from "./PlayerNhlStatsGraph";

const tableTheme = createTheme({
    palette: {
        mode: "dark",
        info: {
            main: "#7ea862",
            secondary: "#7ea862",
        },
        background: {
            default: "#14181d",
            paper: "#14181d",
        },
        text: {
            primary: "#f3f4f6",
            secondary: "#a1a8b3",
        },
    },
    shadows: ["none"],
});

function parseNhlStatsResponse(data) {
    if (typeof data === "string") {
        try {
            return JSON.parse(data);
        } catch {
            return [];
        }
    }
    return Array.isArray(data) ? data : [];
}

function roundNhle(value) {
    if (value == null || Number.isNaN(Number(value))) return null;
    return Math.round(Number(value) * 10) / 10;
}

function applyNhleBasis(row) {
    const factor =
        row.nhle_factor == null || Number.isNaN(Number(row.nhle_factor))
            ? null
            : Number(row.nhle_factor);

    if (factor == null) {
        return {
            ...row,
            goals: null,
            assists: null,
            points: null,
            nhle_applied: false,
            nhle_factor: null,
        };
    }

    const rawGoals = row.goals == null ? null : Number(row.goals);
    const rawAssists = row.assists == null ? null : Number(row.assists);
    const rawPoints =
        row.points == null
            ? rawGoals == null && rawAssists == null
                ? null
                : (rawGoals || 0) + (rawAssists || 0)
            : Number(row.points);

    return {
        ...row,
        goals: rawGoals == null ? null : roundNhle(rawGoals * factor),
        assists: rawAssists == null ? null : roundNhle(rawAssists * factor),
        points: rawPoints == null ? null : roundNhle(rawPoints * factor),
        nhle_applied: true,
        nhle_factor: factor,
    };
}

function buildNhleSkaterColumns() {
    return [
        ...nhlSkaterColumns.slice(0, 3),
        {
            accessorKey: "nhle_factor",
            header: "Factor",
            size: 50,
            Cell: ({ row }) => {
                const factor = row.original.nhle_factor;
                if (factor == null || Number.isNaN(Number(factor))) return "—";
                return Number(factor).toFixed(3);
            },
        },
        ...nhlSkaterColumns.slice(3).map((column) => {
            if (column.accessorKey === "goals") {
                return {
                    ...column,
                    header: "G (NHLe)",
                    Cell: ({ row }) =>
                        row.original.goals == null ? "—" : row.original.goals,
                };
            }
            if (column.accessorKey === "assists") {
                return {
                    ...column,
                    header: "A (NHLe)",
                    Cell: ({ row }) =>
                        row.original.assists == null ? "—" : row.original.assists,
                };
            }
            if (column.accessorKey === "points") {
                return {
                    ...column,
                    header: "PTS (NHLe)",
                    Cell: ({ row }) =>
                        row.original.points == null ? "—" : row.original.points,
                };
            }
            if (column.header === "PPG") {
                return {
                    ...column,
                    header: "PPG (NHLe)",
                    Cell: ({ row }) => {
                        const gp = row.original.games_played;
                        const pts = row.original.points;
                        if (!gp || pts == null) return "—";
                        return parseFloat(pts / gp).toFixed(2);
                    },
                };
            }
            return column;
        }),
    ];
}

function PlayerNhlCareerStatsTab({ playerId, playerData, isActive }) {
    const [stats, setStats] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [displayMode, setDisplayMode] = useState("table");
    const [statBasis, setStatBasis] = useState("raw");

    useEffect(() => {
        if (!playerId || !isActive) {
            return;
        }

        let cancelled = false;
        setLoading(true);
        setError(null);

        publicApi
            .get(`/player_nhl_stats/${playerId}`)
            .then((response) => {
                if (cancelled) return;
                setStats(parseNhlStatsResponse(response.data));
            })
            .catch((err) => {
                if (cancelled) return;
                console.error("Error fetching NHL career stats:", err);
                setError("Failed to load NHL career stats.");
                setStats([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [playerId, isActive]);

    const isGoalie = useMemo(() => {
        if (playerData?.position === "G") {
            return true;
        }
        if (playerData?.position) {
            return false;
        }
        return stats.some(
            (row) => parseFloat(row.save_percentage) > 0 || parseFloat(row.wins) > 0
        );
    }, [playerData, stats]);

    const showNhle = !isGoalie && statBasis === "nhle";

    const displayStats = useMemo(() => {
        if (!showNhle) return stats;
        return stats.map(applyNhleBasis);
    }, [stats, showNhle]);

    const columns = useMemo(() => {
        if (isGoalie) return nhlGoalieColumns;
        return showNhle ? buildNhleSkaterColumns() : nhlSkaterColumns;
    }, [isGoalie, showNhle]);

    if (loading) {
        return (
            <div className="player-nhl-stats-loading">
                <CircularProgress size={28} />
            </div>
        );
    }

    if (error) {
        return (
            <Typography variant="body1" component="div" className="empty-tab-message">
                {error}
            </Typography>
        );
    }

    return (
        <>
            <div className="nhl-stats-header">
                <div>
                    <Typography variant="h4" component="div" className="tab-section-title">
                        NHL Career Stats
                    </Typography>
                    <Typography variant="body2" component="div" className="tab-section-subtitle">
                        {showNhle
                            ? "Goals, assists, and points translated to NHL-equivalent using league NHLe factors."
                            : "Career totals synced from the NHL API across all leagues."}
                    </Typography>
                </div>
                {stats.length > 0 && (
                    <div className="nhl-stats-header-controls">
                        {!isGoalie && (
                            <ToggleButtonGroup
                                value={statBasis}
                                exclusive
                                size="small"
                                onChange={(_, value) => value && setStatBasis(value)}
                                className="nhl-stats-display-toggle"
                                aria-label="Stat basis"
                            >
                                <ToggleButton value="raw">Raw</ToggleButton>
                                <ToggleButton value="nhle">NHL-E</ToggleButton>
                            </ToggleButtonGroup>
                        )}
                        <ToggleButtonGroup
                            value={displayMode}
                            exclusive
                            size="small"
                            onChange={(_, value) => value && setDisplayMode(value)}
                            className="nhl-stats-display-toggle"
                        >
                            <ToggleButton value="table">
                                <TableChartOutlinedIcon fontSize="small" sx={{ mr: 0.5 }} />
                                Table
                            </ToggleButton>
                            <ToggleButton value="graph">
                                <ShowChartOutlinedIcon fontSize="small" sx={{ mr: 0.5 }} />
                                Graph
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </div>
                )}
            </div>

            {stats.length > 0 ? (
                displayMode === "graph" ? (
                    <PlayerNhlStatsGraph
                        stats={displayStats}
                        isGoalie={isGoalie}
                        playerId={playerId}
                    />
                ) : (
                    <ThemeProvider theme={tableTheme}>
                        <MaterialReactTable
                            columns={columns}
                            data={displayStats}
                            initialState={{ density: "compact", pagination: { pageSize: 15 } }}
                        />
                    </ThemeProvider>
                )
            ) : (
                <Typography variant="body1" component="div" className="no-stats-message">
                    No NHL career stats synced for this player yet.
                </Typography>
            )}
        </>
    );
}

export default PlayerNhlCareerStatsTab;
